import { forEachObj } from 'remeda'

import { useRoute } from 'vue-router'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useGtmVar } from '@/composables/app/gtm/useGtmVar'
import { getDocument } from '@/helpers/globals'
import { getAllCookies, useCookie } from '@/plugins/cookie-plugin'
import { useStoreMarketingDataMutation } from '@/composables/app/gtm/useUtmParams.generated.ts'

const utmParamNames = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'fbp',
    'msclkid',
    'ttclid',
    'li_fat_id',
    'twclid',
    'epik',
    'rdt_cid',
    'dclid',
    'qclid',
] as const

export type UtmParamNames = (typeof utmParamNames)[number]
export type UtmParams = Record<UtmParamNames, string>

export async function useUtmParams(): Promise<void> {
    const document = getDocument()

    if (!document) {
        return
    }

    const isAdmin = useIsAdmin()

    if (isAdmin.value) {
        return
    }

    const areUtmParamsSetForSession = useCookie('areUtmParamsSetForSession')

    if (areUtmParamsSetForSession.value === 'true') {
        return
    }

    const utmParams = utmParamNames.reduce(
        (acc, paramName) => ({
            ...acc,
            [paramName]: '',
        }),
        {} as UtmParams
    )

    const query = useRoute().query

    // Store query params from UTM url
    forEachObj(utmParams, (_, paramName) => {
        const urlParam = query[paramName]?.toString()

        if (urlParam) {
            utmParams[paramName] = urlParam
        }
    })

    const allCookies = getAllCookies(document)

    const gtmParams = {
        'clientId': allCookies['_ga'] || '',
        'sessionId': '',
    }

    forEachObj(allCookies, (val, key) => {
        if (key.startsWith('_ga_')) {
            gtmParams.sessionId = val
        }
    })

    if (gtmParams.clientId) {
        useGtmVar('client_id', gtmParams.clientId)
    }

    if (gtmParams.sessionId) {
        useGtmVar('session-id', gtmParams.sessionId)
    }

    const gtmCustomerId = useGtmVar('customer-id')

    const referrer = document.referrer
    const location = document.location.href

    const { executeMutation, data } = useStoreMarketingDataMutation()

    // GraphQL expects camelCase; FO2 collects snake_case URL keys.
    await executeMutation({
        input: {
            utmParams: {
                utmSource: utmParams.utm_source,
                utmMedium: utmParams.utm_medium,
                utmCampaign: utmParams.utm_campaign,
                utmTerm: utmParams.utm_term,
                utmContent: utmParams.utm_content,
                gclid: utmParams.gclid,
                gbraid: utmParams.gbraid,
                wbraid: utmParams.wbraid,
                fbclid: utmParams.fbclid,
                fbp: utmParams.fbp,
                msclkid: utmParams.msclkid,
                ttclid: utmParams.ttclid,
                liFatId: utmParams.li_fat_id,
                twclid: utmParams.twclid,
                epik: utmParams.epik,
                rdtCid: utmParams.rdt_cid,
                dclid: utmParams.dclid,
                qclid: utmParams.qclid,
            },
            gtmParams,
        },
        referrer,
        location,
    })

    const customerId = data.value?.storeMarketingData.customer?.id

    if (customerId) {
        gtmCustomerId.value = customerId
    }

    areUtmParamsSetForSession.value = 'true'
}
